import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { scrollToId } from "@/lib/scroll";
import {
    Search, X, ArrowRight, Camera, Clapperboard, Tag, Mic2, Music4,
    BookOpen, Ticket, Home, FileText, HelpCircle, Image as ImageIcon,
} from "lucide-react";
import { PACKS, EVENT_PACKS, PHOTO_GROUPS, LOCAL_CLIPS, CONCERTS, T } from "@/data/content";

const API = process.env.REACT_APP_BACKEND_URL;

// icône par type de résultat
const ICONS = {
    artist: Mic2,
    clip: Clapperboard,
    pack: Tag,
    event: Camera,
    page: Home,
    photo: ImageIcon,
    track: Music4,
    culture: BookOpen,
    faq: HelpCircle,
};

// pages fixes du site
const PAGE_INDEX = [
    { type: "page", id: "home", fr: "Accueil", en: "Home", kw: "accueil home début gloom" },
    { type: "page", id: "videos", fr: "Clips & Films", en: "Music videos", kw: "clips films vidéos réalisations" },
    { type: "page", id: "photo", fr: "Photographie", en: "Photography", kw: "photo photographie instants shootings" },
    { type: "page", id: "contact", fr: "Tarifs & Contact", en: "Pricing & Contact", kw: "tarifs prix contact devis réserver booking" },
    { type: "page", id: "radio", fr: "GLM Radio", en: "GLM Radio", kw: "radio musique sons playlist suisse" },
    { type: "page", id: "culture", fr: "Culture", en: "Culture", kw: "culture top 10 articles suisse hip-hop" },
    { type: "page", id: "artiste", fr: "Artistes", en: "Artists", kw: "artistes books gloom produits" },
    { type: "page", id: "vip", fr: "VIP", en: "VIP", kw: "vip billet or gold ticket" },
    { type: "page", id: "offre", fr: "Offre", en: "Offer", kw: "offre flèche or son à clipper" },
    { type: "page", id: "faq", fr: "FAQ", en: "FAQ", kw: "faq questions aide" },
    { type: "page", id: "portal", fr: "Mon Espace", en: "My space", kw: "espace compte connexion dashboard admin" },
];

export default function SearchBar() {
    const { lang } = useLang();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [artists, setArtists] = useState([]);
    const [tracks, setTracks] = useState([]);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    // charger les index dynamiques (artistes + sons radio) une fois
    useEffect(() => {
        fetch(`${API}/api/artists`).then((r) => r.json()).then((d) => setArtists(d.artists || [])).catch(() => {});
        fetch(`${API}/api/radio/swiss-tracks`).then((r) => r.json()).then((d) => setTracks((d.tracks || []).slice(0, 30))).catch(() => {});
    }, []);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 80);
    }, [open]);

    // fermer au clic extérieur / Échap
    useEffect(() => {
        const onDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onDown);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onDown);
        };
    }, []);

    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

    const results = useMemo(() => {
        const query = norm(q.trim());
        if (!query) return [];
        const terms = query.split(/\s+/).filter(Boolean);
        const match = (...fields) => {
            const hay = norm(fields.filter(Boolean).join(" "));
            return terms.every((t) => hay.includes(t));
        };
        const out = [];
        // pages
        for (const p of PAGE_INDEX) {
            if (match(p.fr, p.en, p.kw)) out.push({ type: "page", id: p.id, label: lang === "fr" ? p.fr : p.en, sub: "Page" });
        }
        // artistes
        for (const a of artists) {
            if (match(a.artist, a.bio)) {
                out.push({ type: "artist", id: a.slug, label: a.artist, sub: lang === "fr" ? "Artiste Gloom" : "Gloom artist" });
            }
        }
        // clips locaux + concerts + shootings
        for (const c of [...LOCAL_CLIPS, ...CONCERTS]) {
            if (match(c.title, c.client)) out.push({ type: "clip", id: "videos", label: c.title, sub: lang === "fr" ? "Clip" : "Video" });
        }
        for (const g of PHOTO_GROUPS) {
            if (match(g.title, g.sub?.fr, g.sub?.en)) out.push({ type: "photo", id: "photo", label: g.title, sub: lang === "fr" ? "Shooting photo" : "Photo shoot" });
        }
        // packs & événements
        for (const p of PACKS) {
            if (match(p.name?.fr, p.name?.en, "pack tarif prix")) out.push({ type: "pack", id: "contact", label: p.name?.[lang] || p.name?.fr, sub: lang === "fr" ? "Pack tournage" : "Shooting pack", anchor: "#pricing" });
        }
        for (const e of EVENT_PACKS) {
            if (match(e.name?.fr, e.name?.en, "événement")) out.push({ type: "event", id: "contact", label: e.name?.[lang] || e.name?.fr, sub: lang === "fr" ? "Événement" : "Event", anchor: "#pricing" });
        }
        // sons radio
        for (const t of tracks) {
            if (match(t.title)) out.push({ type: "track", id: "radio", label: t.title, sub: "GLM Radio" });
        }
        // créateur 6xo
        if (match("6xo créateur photographe réalisateur directed")) out.push({ type: "artist", id: "creator", label: "6xo", sub: lang === "fr" ? "Créateur — photographe" : "Creator — photographer" });
        return out.slice(0, 9);
    }, [q, artists, tracks, lang]);

    const go = (r) => {
        setOpen(false);
        setQ("");
        if (r.type === "artist") {
            window.__goPage?.("artiste");
            if (r.id === "creator") {
                // drapeau lu par Creator6xo dès que son layout est prêt (scroll multi-passes)
                window.__scrollToCreator = true;
                // si la section est déjà montée, l'événement déclenche le scroll directement
                setTimeout(() => window.dispatchEvent(new Event("creator:scroll")), 150);
            } else {
                setTimeout(() => {
                    const btn = document.querySelector(`[data-testid="roster-${r.id}"]`);
                    btn?.scrollIntoView({ behavior: "smooth", block: "center" });
                    btn?.click();
                }, 700);
            }
            return;
        }
        window.__goPage?.(r.id);
        if (r.anchor) setTimeout(() => scrollToId(r.anchor, -90), 800);
    };

    return (
        <div ref={containerRef} className="relative">
            {/* loupe (mobile) */}
            <button
                data-testid="search-open-btn"
                onClick={() => setOpen((v) => !v)}
                aria-label={lang === "fr" ? "Rechercher" : "Search"}
                className="flex items-center justify-center text-cream/70 transition-colors hover:text-cream lg:hidden"
            >
                <Search size={18} />
            </button>

            {/* barre (desktop) */}
            <div className="relative hidden lg:block">
                <div className={`flex items-center gap-2 border bg-ink/60 backdrop-blur-md transition-all duration-300 ${open ? "border-violet/60 pl-3 pr-2" : "border-white/15 pl-3 pr-3"}`}>
                    <Search size={13} className="shrink-0 text-cream/40" />
                    <input
                        ref={inputRef}
                        data-testid="search-input"
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder={lang === "fr" ? "RECHERCHER…" : "SEARCH…"}
                        className="w-32 bg-transparent py-1.5 font-mono text-[10px] tracking-[0.2em] text-cream outline-none transition-all duration-300 placeholder:text-cream/30 focus:w-44"
                    />
                    {q && (
                        <button onClick={() => { setQ(""); setOpen(false); }} aria-label="clear" className="text-cream/40 hover:text-cream">
                            <X size={12} />
                        </button>
                    )}
                </div>
                {/* résultats desktop */}
                <AnimatePresence>
                    {open && q && (
                        <motion.div
                            data-testid="search-results"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.18 }}
                            className="absolute right-0 top-full z-[80] mt-2 w-80 overflow-hidden border border-white/10 bg-ink/95 backdrop-blur-xl"
                        >
                            {results.length === 0 ? (
                                <p className="px-4 py-4 font-mono text-[10px] tracking-[0.2em] text-cream/40">
                                    {lang === "fr" ? "AUCUN RÉSULTAT" : "NO RESULT"}
                                </p>
                            ) : (
                                results.map((r, i) => {
                                    const Icon = ICONS[r.type] || ArrowRight;
                                    return (
                                        <button
                                            key={`${r.type}-${r.id}-${i}`}
                                            data-testid={`search-result-${i}`}
                                            onClick={() => go(r)}
                                            className="flex w-full items-center gap-3 border-b border-white/5 px-4 py-3 text-left transition-colors last:border-0 hover:bg-violet/10"
                                        >
                                            <Icon size={14} className="shrink-0 text-violet" />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate font-mono text-[10px] tracking-[0.12em] text-cream">{r.label}</span>
                                                <span className="block font-mono text-[8px] tracking-[0.2em] text-cream/40">{r.sub}</span>
                                            </span>
                                            <ArrowRight size={12} className="shrink-0 text-cream/30" />
                                        </button>
                                    );
                                })
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* overlay recherche mobile */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="search-mobile-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
                            <Search size={18} className="shrink-0 text-violet" />
                            <input
                                ref={inputRef}
                                data-testid="search-input-mobile"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={lang === "fr" ? "Rechercher sur le site…" : "Search the site…"}
                                className="flex-1 bg-transparent py-2 font-mono text-sm tracking-[0.1em] text-cream outline-none placeholder:text-cream/30"
                            />
                            <button onClick={() => { setOpen(false); setQ(""); }} aria-label="close" className="text-cream/60">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="max-h-[70vh] overflow-y-auto">
                            {q && results.length === 0 && (
                                <p className="px-5 py-8 text-center font-mono text-[11px] tracking-[0.2em] text-cream/40">
                                    {lang === "fr" ? "AUCUN RÉSULTAT" : "NO RESULT"}
                                </p>
                            )}
                            {results.map((r, i) => {
                                const Icon = ICONS[r.type] || ArrowRight;
                                return (
                                    <button
                                        key={`${r.type}-${r.id}-${i}`}
                                        data-testid={`search-result-m-${i}`}
                                        onClick={() => go(r)}
                                        className="flex w-full items-center gap-4 border-b border-white/5 px-5 py-4 text-left hover:bg-violet/10"
                                    >
                                        <Icon size={16} className="shrink-0 text-violet" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-mono text-[11px] tracking-[0.1em] text-cream">{r.label}</span>
                                            <span className="block font-mono text-[8px] tracking-[0.2em] text-cream/40">{r.sub}</span>
                                        </span>
                                        <ArrowRight size={14} className="shrink-0 text-cream/30" />
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
